<div class="row">
  <div class="col-12">
    <div
      class="page-title-box d-flex flex-wrap align-items-start justify-content-between gap-2"
    >
      <div>
        <h4 class="page-title mb-1">
          {{ "campaign.message.pageTitle" | translate }}
          @if (campaign?.name) {
            <span class="text-dark fw-bold font-20">· {{ campaign.name }}</span>
          }
        </h4>
      </div>
      <div class="d-flex flex-wrap align-items-center gap-2 mt-3">
        <campaign-wallet />
        <campaign-refresh-interval />
      </div>
    </div>
  </div>
</div>

@if (!loadingContent) {
  <div class="campaign-kpis">
    <div class="campaign-kpi">
      <div class="top">
        <div>
          <div class="l">{{ "campaign.message.kpiSent" | translate }}</div>
          <div class="v">{{ totalSentCount | number: "1.0-0" }}</div>
        </div>
        <span class="campaign-kpi-meter" aria-hidden="true">
          <span class="seg is-ok" [style.width.%]="sentPercent"></span>
        </span>
      </div>
      <div class="d">
        {{
          "campaign.message.kpiOfDispatchable"
            | translate: { total: batchTotal }
        }}
        @if (acceptanceRate !== null && totalFailedCount > 0) {
          <span class="sep">·</span>
          {{
            "campaign.message.kpiAcceptanceRate"
              | translate: { percent: acceptanceRate | number: "1.0-1" }
          }}
        }
      </div>
    </div>
    <div class="campaign-kpi">
      <div class="top">
        <div>
          <div class="l">{{ "campaign.message.kpiPending" | translate }}</div>
          <div class="v">{{ totalPendingCount | number: "1.0-0" }}</div>
        </div>
        <span class="campaign-kpi-meter" aria-hidden="true">
          <span class="seg is-queue" [style.width.%]="pendingRate"></span>
        </span>
      </div>
      <div class="d">
        @if (totalPendingCount > 0) {
          {{ "campaign.message.kpiPendingQueuedOnly" | translate }}
        } @else {
          {{ "campaign.message.kpiPendingQueueEmpty" | translate }}
        }
      </div>
    </div>
    <div class="campaign-kpi">
      <div class="top">
        <div>
          <div class="l">{{ "campaign.message.kpiDelivered" | translate }}</div>
          <div class="v">
            @if (receiptsAvailable) {
              {{ deliveredCount | number: "1.0-0" }}
            } @else {
              —
            }
          </div>
        </div>
        @if (receiptsAvailable && batchTotal > 0) {
          <span class="campaign-kpi-meter" aria-hidden="true">
            <span
              class="seg is-ok"
              [style.width.%]="(deliveredCount / batchTotal) * 100"
            ></span>
          </span>
        }
      </div>
      <div class="d">
        @if (!receiptsAvailable) {
          {{ "campaign.message.kpiNoReceipt" | translate }}
        } @else {
          @if (totalFailedCount > 0) {
            <em class="is-err">{{ totalFailedCount | number: "1.0-0" }}</em>
            {{ "campaign.message.kpiFailuresInBatch" | translate }}
          } @else {
            {{ "campaign.message.kpiNoFailures" | translate }}
          }
          @if (unconfirmedCount > 0) {
            <span class="sep">·</span>
            {{
              "campaign.message.kpiUnconfirmedReceipts"
                | translate: { unconfirmed: unconfirmedCount | number: "1.0-0" }
            }}
          }
        }
      </div>
    </div>

    <div class="campaign-kpi">
      <div class="top">
        <div>
          <div class="l">{{ "campaign.message.kpiRead" | translate }}</div>
          <div class="v">
            @if (receiptsAvailable) {
              {{ readCount | number: "1.0-0" }}
            } @else {
              —
            }
          </div>
        </div>
        @if (readRate !== null) {
          <span class="campaign-kpi-meter" aria-hidden="true">
            <span class="seg is-read" [style.width.%]="readRate"></span>
          </span>
        }
      </div>
      <div class="d">
        @if (readRate !== null) {
          {{
            "campaign.message.kpiReadOfDelivered"
              | translate: { percent: readRate | number: "1.0-1" }
          }}
        } @else {
          {{ "campaign.message.kpiNoReceipt" | translate }}
        }
      </div>
    </div>

    @if (supportsClicks) {
      <div class="campaign-kpi">
        <div class="top">
          <div>
            <div class="l">{{ "campaign.message.kpiClicked" | translate }}</div>
            <div class="v">{{ clickedCount | number: "1.0-0" }}</div>
          </div>
          @if (clickRate !== null) {
            <span class="campaign-kpi-meter" aria-hidden="true">
              <span class="seg is-read" [style.width.%]="clickRate"></span>
            </span>
          }
        </div>
        <div class="d">
          @if (clickRate !== null) {
            {{
              "campaign.message.kpiClickedOfDelivered"
                | translate: { percent: clickRate | number: "1.0-1" }
            }}
          } @else {
            {{ "campaign.message.kpiClickedNone" | translate }}
          }
        </div>
      </div>
    }

    <div class="campaign-kpi">
      <div class="top">
        <div>
          <div class="l">
            {{ "campaign.message.kpiDeliveryRate" | translate }}
          </div>
          <div class="v">
            @if (deliveryRate !== null) {
              {{ deliveryRate | number: "1.0-1" }}%
            } @else {
              —
            }
          </div>
        </div>
        @if (deliveryRate !== null) {
          <span class="campaign-kpi-meter" aria-hidden="true">
            <span class="seg is-ok" [style.width.%]="deliveryRate"></span>
          </span>
        }
      </div>
      <div class="d">
        @if (deliveryRate === null) {
          {{ "campaign.message.kpiDeliveryNoSend" | translate }}
        } @else if (!receiptsAvailable) {
          {{ "campaign.message.kpiNoReceipt" | translate }}
        } @else {
          {{
            "campaign.message.kpiDeliveryOfSent"
              | translate
                : {
                    delivered: deliveredCount | number: "1.0-0",
                    sent: totalSentCount | number: "1.0-0",
                  }
          }}
        }
      </div>
    </div>

    <div class="campaign-kpi">
      <div class="top">
        <div>
          <div class="l">
            {{ "campaign.message.kpiRealCost" | translate }}
          </div>
          <div class="v">
            @if (realCost !== null) {
              {{ realCost | currency: "BRL" : "symbol" : "1.2-2" }}
            } @else {
              —
            }
          </div>
        </div>
      </div>
      <div class="d">
        @if (costSettled) {
          {{ "campaign.message.kpiCostSettled" | translate }}
        } @else {
          {{ "campaign.message.kpiCostUnsettled" | translate }}
        }
      </div>
    </div>
  </div>

  <div class="row g-2 mb-2">
    <div class="col-12 col-xl-8">
      <div class="card h-100 mb-0">
        <div class="card-body">
          @if (showProgress) {
            <div
              class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
            >
              <h4 class="header-title mb-0">
                {{ "campaign.message.progressTitle" | translate }}
              </h4>
              <!-- Só "executando" pulsa: é o único estado que muda sozinho. -->
              <span
                class="badge campaign-badge"
                [class.campaign-badge-running]="isRunning"
                [class.is-run]="isRunning"
                [class.is-warn]="!isRunning"
              >
                <span class="campaign-badge-dot"></span>
                {{
                  (isRunning
                    ? "campaign.message.progressRunning"
                    : "campaign.message.statusWaiting"
                  ) | translate
                }}
              </span>
            </div>
            <div
              class="campaign-progress-bar"
              role="img"
              [attr.aria-label]="
                'campaign.message.progressConcluded'
                  | translate: { percent: concludedPercent | number: '1.0-0' }
              "
            >
              @for (segment of progressSegments; track segment.key) {
                <span
                  class="seg is-{{ segment.tone }}"
                  [style.width.%]="segment.percent"
                ></span>
              }
            </div>

            <div class="campaign-progress-legend">
              @for (segment of visibleSegments; track segment.key) {
                <span class="item">
                  <i class="dot is-{{ segment.tone }}"></i>
                  {{ segment.labelKey | translate }}
                  <b>{{ segment.count | number: "1.0-0" }}</b>
                </span>
              }
            </div>
            <div class="campaign-progress-stats">
              <div class="stat">
                <div class="k">
                  {{ "campaign.message.progressConcludedLabel" | translate }}
                </div>
                <div class="v">{{ concludedPercent | number: "1.0-0" }}%</div>
              </div>

              <div class="stat">
                <div class="k">
                  {{ "campaign.message.progressThroughput" | translate }}
                </div>
                <div class="v">
                  @if (throughputPerMinute !== null) {
                    {{ throughputPerMinute | number: "1.0-0" }}
                    <small>{{
                      "campaign.message.progressThroughputUnit" | translate
                    }}</small>
                  } @else {
                    —
                  }
                </div>
              </div>

              <div class="stat">
                <div class="k">
                  {{ "campaign.message.progressEta" | translate }}
                </div>
                <div class="v">{{ remainingLabel ?? "—" }}</div>
              </div>
            </div>

            @if (withheldCount > 0) {
              <p class="campaign-progress-note mb-0">
                <i class="mdi mdi-information-outline"></i>
                {{
                  "campaign.message.progressWithheld"
                    | translate: { count: withheldCount }
                }}
              </p>
            }
          } @else {
            <h4 class="header-title mb-1">
              {{ "campaign.message.chartTitle" | translate }}
            </h4>
            <p class="text-muted mb-2">
              {{ "campaign.message.chartSubtitle" | translate }}
              @if (latency && !hasReceipts) {
                <span class="ms-1">
                  <i class="mdi mdi-information-outline"></i>
                  {{ "campaign.message.chartNoReceipt" | translate }}
                </span>
              }
            </p>

            @if (latencyChart.length) {
              <!-- The one-line answer: how long until half arrived and half was read. -->
              <div class="campaign-progress-stats mb-3">
                <div class="stat">
                  <div class="k">
                    {{ "campaign.message.chartHalfDelivered" | translate }}
                  </div>
                  <div class="v">{{ halfDeliveredLabel }}</div>
                </div>
                <div class="stat">
                  <div class="k">
                    {{ "campaign.message.chartHalfRead" | translate }}
                  </div>
                  <div class="v">{{ halfReadLabel }}</div>
                </div>
              </div>

              <div class="chart-legend d-flex flex-wrap gap-3 mb-2">
                @for (name of chartSeriesNames; track name; let i = $index) {
                  <span class="d-inline-flex align-items-center gap-1">
                    <i
                      class="dot"
                      [style.background-color]="chartColors.domain[i]"
                    ></i>
                    {{ name }}
                  </span>
                }
              </div>

              <div class="chart-card">
                <ngx-charts-line-chart
                  [results]="latencyChart"
                  [scheme]="chartColors"
                  [xAxis]="true"
                  [yAxis]="true"
                  [timeline]="false"
                  [roundDomains]="true"
                  [yScaleMin]="0"
                  [legend]="false"
                  [yAxisTickFormatting]="formatCount"
                ></ngx-charts-line-chart>
              </div>
            } @else {
              <empty-records-component
                [message]="'campaign.message.chartEmpty' | translate"
                [icon]="'mdi mdi-chart-line'"
              />
            }
          }
        </div>
      </div>
    </div>

    <div class="col-12 col-xl-4">
      <div class="card h-100 mb-0">
        <div class="card-body">
          <div class="d-flex align-items-center mb-2">
            <i
              class="mdi fs-3 rounded-circle p-2"
              [ngClass]="{
                'mdi-check-circle text-success':
                  campaign?.status === campaignStatusEnum.DONE,
                'mdi-timer-sand text-warning':
                  campaign?.status === campaignStatusEnum.SENDING,
                'mdi-alert-circle text-danger':
                  campaign?.status === campaignStatusEnum.ERROR,
                'mdi-cancel text-secondary':
                  campaign?.status === campaignStatusEnum.CANCELED,
                'mdi-clock-outline text-light':
                  campaign?.status === campaignStatusEnum.WAITING,
              }"
            ></i>
            <h3 class="fs-3">{{ translateStatus($safeNavigationMigration(campaign?.status)) }}</h3>
          </div>

          @if (failureMessage) {
            <div class="alert alert-danger py-2 mb-3" role="alert">
              <h5 class="alert-heading mt-0 mb-1">
                <i class="mdi mdi-alert-circle-outline me-1"></i>
                {{ "campaign.message.failureReason" | translate }}
              </h5>
              <p class="mb-0">{{ failureMessage }}</p>
            </div>
          }

          <div class="d-flex flex-column gap-2 fs-5 text-dark">
            <div class="d-flex justify-content-between align-items-center">
              {{ "campaign.message.dispatchType" | translate }}
              <span class="">
                {{
                  (campaign.executionType === "IMEDIATE"
                    ? "campaign.message.dispatchImmediate"
                    : "campaign.message.dispatchScheduled"
                  ) | translate
                }}
              </span>
            </div>
            @if (
              campaign.channel.type !== "CLOUD_API" &&
              campaign?.delayInSeconds > 0
            ) {
              <div class="d-flex justify-content-between align-items-center">
                {{ "campaign.message.delayBetweenMessages" | translate }}
                <span class="">
                  {{ campaign?.delayInSeconds }}
                  {{ "campaign.message.secondsSuffix" | translate }}
                </span>
              </div>
            }
            <div class="d-flex justify-content-between align-items-center">
              {{ "campaign.message.startDate" | translate }}
              <span class="">
                {{
                  $safeNavigationMigration(campaign?.startedAt)
                    | timezoneDate
                      : "DD/MM/YYYY HH:mm:ss"
                      : getTimezoneCompanyCurrentUser()
                }}
              </span>
            </div>
            @if (campaign?.endedAt) {
              <div class="d-flex justify-content-between align-items-center">
                {{ "campaign.message.endDate" | translate }}
                <span class="">
                  {{
                    $safeNavigationMigration(campaign?.endedAt)
                      | timezoneDate
                        : "DD/MM/YYYY HH:mm:ss"
                        : getTimezoneCompanyCurrentUser()
                  }}
                </span>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                {{ "campaign.message.duration" | translate }}
                <span class="text-green-500">
                  <b>{{ getTimeSpent() }}</b>
                </span>
              </div>
            }
            <div class="d-flex justify-content-between align-items-start gap-2">
              {{ "campaign.message.targetAudience" | translate }}
              <span class="d-flex flex-wrap justify-content-end gap-1">
                @for (tag of formatTags(); track $index) {
                  <span
                    class="badge"
                    [ngStyle]="{
                      'background-color': tag.bgColor,
                      color: (tag.bgColor | readableText: tag.fontColor),
                    }"
                  >
                    {{ tag.title }}
                  </span>
                } @empty {
                  <span class="text-muted">{{
                    "campaign.message.targetAudienceEmpty" | translate
                  }}</span>
                }
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="row g-2">
    <div [class]="hasPreview && previewOpen ? 'col-12 col-xl-8' : 'col-12'">
      <div class="card mb-0">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="header-title mb-0">
              {{ "campaign.message.detailsTitle" | translate }}
            </h4>
            @if (hasPreview) {
              <button
                type="button"
                class="btn btn-sm btn-soft-primary"
                (click)="togglePreview()"
                [attr.aria-expanded]="previewOpen"
              >
                <i
                  class="ri-eye-line me-1"
                  [class.ri-eye-off-line]="previewOpen"
                  [class.ri-eye-line]="!previewOpen"
                ></i>
                {{
                  (previewOpen
                    ? "campaign.message.hidePreview"
                    : "campaign.message.showPreview"
                  ) | translate
                }}
              </button>
            }
          </div>
          <mat-select class="form-control" [formControl]="statusControl">
            <mat-select-trigger>
              {{
                (statusTranslations[statusControl.value] | translate) ||
                  ("campaign.message.selectStatus" | translate)
              }}
            </mat-select-trigger>
            @for (status of statusList; track $index) {
              <mat-option [value]="status">
                {{ statusTranslations[status] | translate }}
              </mat-option>
            }
          </mat-select>
        </div>
        <div class="card-body table-responsive pt-0">
          @if (pager.total > 0) {
            <table class="table table-hover table-centered mb-0">
              <thead class="table-light">
                <tr>
                  <th>{{ "campaign.message.colName" | translate }}</th>
                  <th>{{ "campaign.message.colPhone" | translate }}</th>
                  <th>{{ "campaign.message.colStatus" | translate }}</th>
                  <th>{{ "campaign.message.chartSent" | translate }}</th>
                  <th>{{ "campaign.message.chartDelivered" | translate }}</th>
                  <th>{{ "campaign.message.chartRead" | translate }}</th>
                  @if (supportsClicks) {
                    <th>{{ "campaign.message.chartClicked" | translate }}</th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (item of pager.list; track item._id) {
                  <tr class="cursor-pointer" (click)="view(item)">
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="avatar me-2 rounded-circle">
                          <img
                            class="img-fluid avatar-sm rounded-circle"
                            [src]="getContactImage(item.contact)"
                            [alt]="recipientName(item)"
                            (error)="onImageError($event)"
                          />
                        </div>
                        @if (recipientName(item)) {
                          <span>{{ recipientName(item) }}</span>
                        } @else {
                          <span class="text-muted">—</span>
                        }
                      </div>
                    </td>
                    <td>{{ recipientPhone(item) }}</td>
                    <td>
                      @switch (resolveMessageStatus(item)) {
                        @case ("SUCCESS") {
                          <span class="badge badge-success-lighten">
                            {{ "campaign.message.statusSuccess" | translate }}
                          </span>
                        }
                        @case ("ERROR") {
                          <span class="badge badge-danger-lighten">
                            {{ "campaign.message.statusError" | translate }}
                          </span>
                        }
                        @case ("EXCEPTION") {
                          <span class="badge badge-danger-lighten">
                            {{ "campaign.message.statusError" | translate }}
                          </span>
                        }
                        @case ("PENDING") {
                          <span class="badge badge-warning-lighten">
                            {{ "campaign.message.statusPending" | translate }}
                          </span>
                        }
                        @default {
                          <span class="text-muted">—</span>
                        }
                      }
                    </td>
                    <td>
                      @if (item.sentAt) {
                        {{
                          item.sentAt
                            | timezoneDate
                              : "HH:mm:ss"
                              : getTimezoneCompanyCurrentUser()
                        }}
                      } @else {
                        <span class="text-muted">—</span>
                      }
                    </td>
                    <td>
                      @if (item.deliveredAt) {
                        {{
                          item.deliveredAt
                            | timezoneDate
                              : "HH:mm:ss"
                              : getTimezoneCompanyCurrentUser()
                        }}
                      } @else {
                        <span class="text-muted">—</span>
                      }
                    </td>
                    <td>
                      @if (item.readAt) {
                        {{
                          item.readAt
                            | timezoneDate
                              : "HH:mm:ss"
                              : getTimezoneCompanyCurrentUser()
                        }}
                      } @else {
                        <span class="text-muted">—</span>
                      }
                    </td>
                    @if (supportsClicks) {
                    <td>
                      @if (item.clicked) {
                        <div class="d-flex flex-column">
                          <span class="badge campaign-badge is-ok align-self-start">{{
                            item.clicks?.[0]?.text ||
                              ("campaign.messagePayload.clicked" | translate)
                          }}</span>
                          @if (item.clickedAt) {
                            <small class="text-muted">{{
                              item.clickedAt
                                | timezoneDate
                                  : "HH:mm:ss"
                                  : getTimezoneCompanyCurrentUser()
                            }}</small>
                          }
                        </div>
                      } @else {
                        <span class="text-muted">—</span>
                      }
                    </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
            @if (pager.total > pager.perPage) {
              <paginator [pager]="pager" (onPager)="loadPage($event)" />
            }
          }
          @if (pager.total === 0) {
            <empty-records-component
              [message]="'campaign.message.emptyMessage' | translate"
              [icon]="'mdi mdi-alert-circle-outline'"
              [subMessage]="'campaign.message.emptySubMessage' | translate"
            />
          }
        </div>
      </div>
    </div>

    @if (hasPreview && previewOpen) {
      <div class="col-12 col-xl-4">
        <div class="card mb-0">
          <div class="card-body">
            <h4 class="header-title mb-3">
              {{ "campaign.message.previewTitle" | translate }}
            </h4>
            <div class="d-flex justify-content-center">
              @if (zapiPreviewData) {
                @if (isZapiButtonsPreview) {
                  <chat-wa-preview
                    [title]="zapiPreviewData.buttonsTitle"
                    [message]="zapiPreviewData.buttonsMessage"
                    [footer]="zapiPreviewData.buttonsFooter"
                    [buttons]="$any(zapiPreviewData).buttonActions"
                    [templateType]="'button'"
                    [showChatFrame]="false"
                  />
                } @else {
                  <campaign-evo-template [zapiData]="zapiPreviewData" />
                }
              } @else if (rcsPreviewData) {
                <rcs-message-preview [rcsData]="rcsPreviewData" />
              } @else {
                <campaign-cloud-api-template
                  [cloudAPIData]="cloudApiPreviewData"
                />
              }
            </div>
          </div>
        </div>
      </div>
    }
  </div>
} @else {
  <loading-component />
}

<hr class="mt-4" />

<div class="row mt-4">
  <div
    class="col-12 gap-2 d-flex justify-content-end pb-4 align-items-baseline"
  >
    @if (isAdmin() && canRestartOrContinue) {
      <button
        class="btn btn-outline-primary"
        (click)="restartOrContinue()"
        [disabled]="loading"
      >
        @if (!loading) {
          <i class="ri-restart-line"></i>
          {{ "campaign.message.continue" | translate }}
        } @else {
          <mat-spinner [diameter]="25" mode="indeterminate" />
        }
      </button>
    }

    <button
      type="button"
      class="btn btn-outline-secondary"
      [disabled]="loading"
      (click)="goBack()"
    >
      {{ "campaign.message.back" | translate }}
    </button>

    <button
      class="btn btn-outline-primary"
      (click)="updatePage()"
      [disabled]="loading"
    >
      @if (!loading) {
        <i class="ri-refresh-line"></i>
        {{ "campaign.message.sync" | translate }}
      } @else {
        <mat-spinner [diameter]="25" mode="indeterminate" />
      }
    </button>
  </div>
</div>
